import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  BORDER,
  INVENTORY_PRODUCT_CONTENT_W,
  INVENTORY_PRODUCT_MARGIN,
  INVENTORY_PRODUCT_X0,
  LINE_COLOR,
  drawInventoryByProductReportFooter,
  drawInventoryByProductReportHeader,
  formatQtyCell,
  loadLedgerLogos,
  sumNumeric,
} from "./ledgerStatementPdfShared";

/** Nine column widths (pt) — must sum exactly to INVENTORY_PRODUCT_CONTENT_W (495.28). */
function buildInventoryProductColumnStyles() {
  const tableW = INVENTORY_PRODUCT_CONTENT_W;
  const widths = [52, 94, 68, 32, 42.32, 42.32, 42.32, 42.32, 69.68];
  const sum = widths.reduce((acc, w) => acc + w, 0);
  widths[8] += tableW - sum;

  const styles = {};
  widths.forEach((cellWidth, i) => {
    styles[i] = {
      cellWidth,
      halign: i <= 2 ? "left" : i === 3 ? "center" : "right",
      overflow: i === 3 ? "ellipsize" : "linebreak",
    };
  });
  return styles;
}

const INVENTORY_PRODUCT_TABLE_STYLES = {
  theme: "grid",
  tableWidth: INVENTORY_PRODUCT_CONTENT_W,
  styles: {
    font: "times",
    fontSize: 7,
    cellPadding: { top: 3, right: 2.5, bottom: 3, left: 2.5 },
    lineWidth: BORDER,
    lineColor: LINE_COLOR,
    textColor: [0, 0, 0],
    overflow: "linebreak",
    valign: "middle",
    minCellHeight: 14,
    halign: "left",
  },
  headStyles: {
    fillColor: [255, 255, 255],
    textColor: [0, 0, 0],
    fontStyle: "bold",
    halign: "center",
    valign: "middle",
    minCellHeight: 24,
    fontSize: 7.5,
    cellPadding: 3,
    lineWidth: BORDER,
    lineColor: LINE_COLOR,
  },
  bodyStyles: {
    lineWidth: BORDER,
    lineColor: LINE_COLOR,
  },
};

function applyProductTableCellStyles(data) {
  if (data.section === "body" && data.row.index === 0) {
    data.cell.styles.fontStyle = "bold";
    data.cell.styles.fillColor = [248, 249, 250];
    if (data.column.index >= 4) {
      data.cell.styles.halign = "right";
    } else if (data.column.index === 3) {
      data.cell.styles.halign = "center";
    } else {
      data.cell.styles.halign = "left";
    }
  }
}

/**
 * Build A4 portrait STOCK LEDGER REPORT PDF from API product rows (page box matches GRN PDF).
 */
export async function exportInventoryByProductReportPdf(rows, filename, context = {}) {
  const productRows = Array.isArray(rows) ? rows : [];

  const totalRow = [
    "Total",
    "",
    "",
    "",
    formatQtyCell(sumNumeric(productRows.map((r) => r.openingStock))),
    formatQtyCell(sumNumeric(productRows.map((r) => r.inwardQuantity))),
    formatQtyCell(sumNumeric(productRows.map((r) => r.outwardQuantity))),
    formatQtyCell(sumNumeric(productRows.map((r) => r.returnQuantity))),
    formatQtyCell(sumNumeric(productRows.map((r) => r.closingStock))),
  ];

  const bodyRows = productRows.map((row) => [
    row.productCode || "",
    row.productDescription || "",
    row.companyName || "—",
    row.uom || "",
    formatQtyCell(row.openingStock),
    formatQtyCell(row.inwardQuantity),
    formatQtyCell(row.outwardQuantity),
    formatQtyCell(row.returnQuantity),
    formatQtyCell(row.closingStock),
  ]);

  const logos = await loadLedgerLogos();

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
  });

  const tableStartY = drawInventoryByProductReportHeader(doc, context, logos);

  autoTable(doc, {
    startY: tableStartY,
    margin: {
      left: INVENTORY_PRODUCT_X0,
      right: INVENTORY_PRODUCT_X0,
      bottom: INVENTORY_PRODUCT_MARGIN + 96,
    },
    head: [
      [
        "Product Code",
        "SKU Name",
        "Company",
        "UOM",
        "Opening",
        "Inward",
        "Outward",
        "Return",
        "Closing",
      ],
    ],
    body: [totalRow, ...bodyRows],
    ...INVENTORY_PRODUCT_TABLE_STYLES,
    columnStyles: buildInventoryProductColumnStyles(),
    showHead: "everyPage",
    rowPageBreak: "avoid",
    horizontalPageBreak: false,
    didParseCell: applyProductTableCellStyles,
  });

  drawInventoryByProductReportFooter(doc, context, doc.lastAutoTable.finalY);

  doc.save(filename);
}
